import React, { useState } from 'react';
import axios from 'axios';
import { Sidebar } from './Dashboard';
import supabase from '../lib/supabase';

const API = 'https://tribaltech-backend.onrender.com';

// Upload flow (two modes, same plumbing):
//
//   mode = 'auto'
//     1. presign (bucket=ppv-originals)
//     2. upload source → Supabase
//     3. confirm { storage_path, ...meta }
//        backend kicks wavmark_api /ppv/preview to generate 30s clip
//
//   mode = 'manual'
//     1. presign (bucket=ppv-originals)
//     2. upload source → Supabase
//     3. presign (bucket=ppv-previews)
//     4. upload preview → Supabase
//     5. confirm { storage_path, preview_storage_path, ...meta }
//        backend marks preview_status=ready, skips auto-generation

export default function PpvUpload() {
  const [mode, setMode] = useState('auto'); // 'auto' | 'manual'
  const [file, setFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceDollars, setPriceDollars] = useState('');
  // Compliance — performer records drive the 2257 intro overlay
  // burned into the first 5s of every new upload.
  const [performers, setPerformers] = useState([
    { name: '', age_confirmed_18_plus: false, consent_confirmed: false },
  ]);
  const [progress, setProgress] = useState(0);
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const [listing, setListing] = useState(null);

  function updatePerformer(i, patch) {
    setPerformers(list => list.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  }
  function addPerformer() {
    setPerformers(list => [...list, { name: '', age_confirmed_18_plus: false, consent_confirmed: false }]);
  }
  function removePerformer(i) {
    setPerformers(list => list.length <= 1 ? list : list.filter((_, idx) => idx !== i));
  }

  const token = localStorage.getItem('tribal_token');

  async function presign(filename, contentType, bucket) {
    const r = await axios.post(API + '/api/ppv/listings/presign',
      { filename, content_type: contentType, bucket },
      { headers: { Authorization: 'Bearer ' + token } },
    );
    return r.data; // { storage_path, token, signed_url, bucket, content_type }
  }

  async function uploadToBucket(bucket, storage_path, uploadToken, fileObj) {
    const { error: upErr } = await supabase.storage
      .from(bucket)
      .uploadToSignedUrl(storage_path, uploadToken, fileObj, {
        contentType: fileObj.type || 'application/octet-stream',
        upsert: false,
      });
    if (upErr) throw new Error(`upload to ${bucket} failed: ${upErr.message}`);
  }

  async function upload() {
    if (!file || !title || !priceDollars) {
      setError('Pick a source video, set a title, and set a price.');
      return;
    }
    if (mode === 'manual' && !previewFile) {
      setError('Manual preview mode — select a preview clip to upload.');
      return;
    }
    const priceCents = Math.round(parseFloat(priceDollars) * 100);
    if (!Number.isFinite(priceCents) || priceCents < 50) {
      setError('Price must be at least $0.50');
      return;
    }
    // Compliance validation — every performer needs a name + both
    // checkboxes ticked before we'll take the upload.
    const cleanedPerformers = performers.map(p => ({
      performer_name: (p.name || '').trim(),
      age_confirmed_18_plus: !!p.age_confirmed_18_plus,
      consent_confirmed: !!p.consent_confirmed,
    })).filter(p => p.performer_name);
    if (cleanedPerformers.length === 0) {
      setError('Add at least one performer.');
      return;
    }
    for (const p of cleanedPerformers) {
      if (!p.age_confirmed_18_plus || !p.consent_confirmed) {
        setError(`Confirm age and consent for ${p.performer_name}.`);
        return;
      }
    }
    setError(''); setState('presigning'); setProgress(0);

    try {
      // Step 1/2: upload the source to ppv-originals
      const src = await presign(file.name, file.type || 'video/mp4', 'ppv-originals');
      setState('uploading-source');
      await uploadToBucket('ppv-originals', src.storage_path, src.token, file);
      setProgress(50);

      // Step 3/4: if manual, upload the preview to ppv-previews
      let previewStoragePath = null;
      if (mode === 'manual') {
        setState('presigning-preview');
        const prev = await presign(previewFile.name, previewFile.type || 'video/mp4', 'ppv-previews');
        setState('uploading-preview');
        await uploadToBucket('ppv-previews', prev.storage_path, prev.token, previewFile);
        previewStoragePath = prev.storage_path;
      }
      setProgress(90);

      // Step 5: confirm
      setState('confirming');
      const confirm = await axios.post(API + '/api/ppv/listings/confirm', {
        storage_path: src.storage_path,
        title,
        description,
        price_cents: priceCents,
        file_size_bytes: file.size,
        performers: cleanedPerformers,
        ...(previewStoragePath ? { preview_storage_path: previewStoragePath } : {}),
      }, { headers: { Authorization: 'Bearer ' + token } });

      setListing(confirm.data.listing);
      setProgress(100);
      setState('done');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'upload failed');
      setState('error');
    }
  }

  const working = ['presigning','uploading-source','presigning-preview','uploading-preview','confirming'].includes(state);

  return (
    <div className="page">
      <Sidebar />
      <div className="main">
        <div className="page-title">Upload PPV video</div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, marginBottom: 10 }}>Preview</h3>
          <div style={{ display:'flex', flexDirection:'column', gap: 8, marginBottom: 10 }}>
            <label style={{ display:'flex', alignItems:'flex-start', gap: 10, cursor:'pointer' }}>
              <input
                type="radio"
                name="preview-mode"
                checked={mode === 'auto'}
                onChange={() => setMode('auto')}
                disabled={working}
                style={{ marginTop: 3 }}
              />
              <div>
                <div style={{ fontSize: 13, color:'#fff' }}>Auto-generate a 30s preview</div>
                <div style={{ fontSize: 11, color:'var(--muted)' }}>
                  Creator Protect cuts the first 30 seconds of your source video, muted, and uses it as the teaser.
                </div>
              </div>
            </label>
            <label style={{ display:'flex', alignItems:'flex-start', gap: 10, cursor:'pointer' }}>
              <input
                type="radio"
                name="preview-mode"
                checked={mode === 'manual'}
                onChange={() => setMode('manual')}
                disabled={working}
                style={{ marginTop: 3 }}
              />
              <div>
                <div style={{ fontSize: 13, color:'#fff' }}>Upload my own preview clip</div>
                <div style={{ fontSize: 11, color:'var(--muted)' }}>
                  Skip auto-generation. The clip you upload is what buyers see on the public page.
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, marginBottom: 4 }}>Source video</h3>
          <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 14 }}>
            The full video buyers will receive after purchase.
          </div>
          <input
            type="file"
            accept="video/mp4,video/quicktime,video/*"
            onChange={e => setFile(e.target.files[0])}
            disabled={working}
          />
          {file && (
            <div style={{ fontSize: 11, color:'var(--muted)', marginTop: 6 }}>
              {file.name} — {(file.size / (1024*1024)).toFixed(1)} MB
            </div>
          )}
        </div>

        {mode === 'manual' && (
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 15, marginBottom: 4 }}>Preview clip</h3>
            <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 14 }}>
              Short teaser, typically 15–30 seconds. Shown publicly on the buy page.
            </div>
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/*"
              onChange={e => setPreviewFile(e.target.files[0])}
              disabled={working}
            />
            {previewFile && (
              <div style={{ fontSize: 11, color:'var(--muted)', marginTop: 6 }}>
                {previewFile.name} — {(previewFile.size / (1024*1024)).toFixed(1)} MB
              </div>
            )}
          </div>
        )}

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, marginBottom: 4 }}>Performers & compliance</h3>
          <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 14 }}>
            Required under 18 U.S.C. §2257. A 5-second intro overlay listing these
            names + compliance attestation is burned into the video.
          </div>
          {performers.map((p, i) => (
            <div key={i} style={{ padding: 12, background:'#0a0a0a', border:'1px solid #1a1a1a', borderRadius: 8, marginBottom: 10 }}>
              <div style={{ display:'flex', gap: 8, alignItems:'center', marginBottom: 10 }}>
                <input
                  type="text"
                  value={p.name}
                  onChange={e => updatePerformer(i, { name: e.target.value })}
                  placeholder="Performer name"
                  disabled={working}
                  style={{ flex: 1 }}
                />
                {performers.length > 1 && (
                  <button type="button" onClick={() => removePerformer(i)} disabled={working}
                          style={{ padding:'6px 10px', borderRadius: 6, border:'1px solid #333', background:'transparent', color:'#94a3b8', fontSize: 11, cursor:'pointer' }}>
                    Remove
                  </button>
                )}
              </div>
              <label style={{ display:'flex', alignItems:'flex-start', gap: 8, fontSize: 12, color:'#cbd5e1', marginBottom: 6, cursor:'pointer' }}>
                <input
                  type="checkbox"
                  checked={p.age_confirmed_18_plus}
                  onChange={e => updatePerformer(i, { age_confirmed_18_plus: e.target.checked })}
                  disabled={working}
                  style={{ marginTop: 2 }}
                />
                <span>I confirm this performer is 18 years of age or older, and I have government-issued ID on file.</span>
              </label>
              <label style={{ display:'flex', alignItems:'flex-start', gap: 8, fontSize: 12, color:'#cbd5e1', cursor:'pointer' }}>
                <input
                  type="checkbox"
                  checked={p.consent_confirmed}
                  onChange={e => updatePerformer(i, { consent_confirmed: e.target.checked })}
                  disabled={working}
                  style={{ marginTop: 2 }}
                />
                <span>This performer has given explicit consent for this content to be distributed.</span>
              </label>
            </div>
          ))}
          <button type="button" onClick={addPerformer} disabled={working}
                  style={{ padding:'6px 12px', borderRadius: 6, border:'1px solid #333', background:'transparent', color:'#a78bfa', fontSize: 12, cursor:'pointer' }}>
            + Add another performer
          </button>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, marginBottom: 16 }}>Details</h3>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, color:'var(--muted)', display:'block', marginBottom: 6 }}>Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Give buyers something to click" disabled={working} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, color:'var(--muted)', display:'block', marginBottom: 6 }}>Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              disabled={working}
              style={{ width:'100%', padding: 10, borderRadius: 8, background:'#1a1a1a', border:'1px solid #333', color:'#fff', fontSize: 14, resize:'vertical', boxSizing:'border-box' }}
              placeholder="Optional — a sentence or two"
            />
          </div>
          <div style={{ marginBottom: 14, maxWidth: 200 }}>
            <label style={{ fontSize: 13, color:'var(--muted)', display:'block', marginBottom: 6 }}>Price (USD)</label>
            <input
              type="number"
              min="0.50"
              step="0.01"
              value={priceDollars}
              onChange={e => setPriceDollars(e.target.value)}
              placeholder="9.99"
              disabled={working}
            />
          </div>
        </div>

        {error && (
          <div className="card" style={{ marginBottom: 20, borderLeft:'3px solid var(--coral)' }}>
            <div style={{ fontSize: 13, color:'var(--coral)' }}>{error}</div>
          </div>
        )}

        {working && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, color:'var(--muted)', marginBottom: 6 }}>
              {state === 'presigning'         && 'Requesting upload URL…'}
              {state === 'uploading-source'   && 'Uploading source video…'}
              {state === 'presigning-preview' && 'Requesting preview URL…'}
              {state === 'uploading-preview'  && 'Uploading preview clip…'}
              {state === 'confirming'         && 'Creating listing…'}
            </div>
            <div style={{ height: 6, background:'rgba(255,255,255,0.06)', borderRadius: 3 }}>
              <div style={{ width: progress + '%', height: '100%', background:'var(--purple-light)', borderRadius: 3 }} />
            </div>
          </div>
        )}

        {state === 'done' && listing && (
          <div className="card" style={{ marginBottom: 20, borderLeft:'3px solid var(--green)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Uploaded</div>
            <div style={{ fontSize: 12, color:'var(--muted)', marginBottom: 10 }}>
              Listing created in <code>draft</code>. Preview status:{' '}
              <code>{listing.preview_status}</code>
              {listing.preview_status === 'pending' && ' (usually ready in ~30s)'}.
            </div>
            <a href={`/ppv/${listing.id}`} className="btn btn-secondary" style={{ marginRight: 8 }}>View public page</a>
            <a href="/dashboard?tab=vault" className="btn btn-primary">Go to My Vault</a>
          </div>
        )}

        <button
          className="btn btn-primary"
          onClick={upload}
          disabled={working || state === 'done'}
          style={{ minWidth: 200 }}
        >
          {state === 'done'     ? 'Uploaded' :
           state === 'presigning' ? 'Preparing…' :
           state.startsWith('uploading') ? `Uploading… ${progress}%` :
           state === 'confirming' ? 'Finalizing…' :
           'Upload & create listing'}
        </button>
      </div>
    </div>
  );
}
